/**
 * The unfoldable depth (SPEC §6.2, §6.3).
 *
 * Fragile competencies first, then those ahead, then the full map for whoever
 * wants to dig. Uncertainty is visible throughout: a competency seen twice is
 * drawn dimmed and says "peu observé" in words — it is never presented with the
 * same confidence as one seen thirty times.
 *
 * **Deux cartes de plus depuis le 6 septembre 2026** : « Niveau actuel », une
 * position d'ensemble pondérée par les coefficients de l'établissement, et
 * « Vu en classe, jamais mesuré ici », le trou entre l'école et l'application.
 * Le pourquoi de chacune est sur son composant, plus bas.
 *
 * **La carte complète porte trois filtres**, demandés par Hedi le 28 août 2026.
 * Elle liste tout le programme — 114 compétences pour Baya — et une liste de
 * cette longueur ne se parcourt pas : le parent lit les deux cartes du dessus et
 * n'ouvre plus la troisième. Les axes sont la matière, l'état de maîtrise, et
 * évaluée ou non ; le pourquoi de chacun est dans `parent/progression`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { Dashboard } from "@/api/schema";
import { isMasteryState, PARENT_STATE_LABEL } from "@/config/wording";
import { phraseDePenurie } from "@/ui/penurie";
import {
  ahead,
  ETATS_EVALUES,
  filtreActif,
  filtrerCompetences,
  fragile,
  phraseDuVide,
  readProgression,
  resumeDuFiltre,
  resumeDuNiveau,
  SANS_FILTRE,
  type CompetencyReading,
  type FiltreCompetences,
  type Mesure,
  type Progression,
  type TaughtCompetency,
} from "@/parent/progression";
import { WorkDoneCard } from "@/parent/WorkDoneCard";
import { Gauge } from "@/ui/Gauge";
import { Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";
import { StateChip } from "@/ui/StateChip";
import { VuEnClasse } from "@/ui/VuEnClasse";
import { etiquette } from "@/ui/matieres";

export function ChildDetailScreen() {
  const { childId = "" } = useParams();
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [progression, setProgression] = useState<Progression | null>(null);
  const [error, setError] = useState<string | null>(null);
  const demande = useRef(0);

  /**
   * Recharger le tableau de bord — au montage, et après une déclaration.
   *
   * Extrait de l'effet parce qu'il a désormais un second appelant : le bouton
   * « Oui, c'est vu » de la carte « Vu en classe ». La compétence validée doit
   * réapparaître dans la liste attestée **avec sa cause de pénurie**, ce qui est
   * son vrai nouvel état ; la retirer de l'écran sans relire montrerait moins
   * que ce qui vient de se passer.
   *
   * Le compteur remplace le drapeau que portait la fermeture de l'effet : une
   * réponse arrivée après un changement d'enfant — ou après un rechargement plus
   * récent — n'écrase pas ce qui est affiché.
   */
  const charger = useCallback(async () => {
    const mien = ++demande.current;
    const loaded = await call("dashboard", { params: { child_id: childId } });
    if (mien !== demande.current) return;
    setDashboard(loaded);
    setProgression(readProgression(loaded.progression));
  }, [childId]);

  useEffect(() => {
    let annule = false;
    void charger().catch((failure) => {
      if (annule) return;
      setError(failure instanceof ApiError ? failure.message : "Chargement impossible.");
    });
    return () => {
      annule = true;
    };
  }, [charger]);

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!dashboard) return <Loading />;

  return (
    <Screen
      title={dashboard.display_name}
      lead={dashboard.level}
    >
      <p className="notice">
        Vous voyez des résumés, des compétences et des difficultés. Jamais les conversations.
      </p>

      {/* Avant le conditionnel : le travail fourni ne dépend pas d'une mesure de
          maîtrise. `mastery` a besoin de plusieurs observations avant d'affirmer
          quoi que ce soit ; les exercices rendus, eux, existent dès le premier
          soir — et c'est ce soir-là qu'un parent regarde. */}
      <WorkDoneCard work={dashboard.work} />

      {/* La suite naturelle de la carte du dessus : « et sur un mois ? ». Posée
          ici plutôt que dans les seuls sous-onglets parce que c'est là que la
          question se forme, une fois la semaine lue. */}
      <p className="deep-link">
        <Link to={`/parents/enfant/${childId}/effort`}>
          Voir le détail sur une période →
        </Link>{" "}
        <Link to={`/parents/enfant/${childId}/cahier`}>Voir les énoncés travaillés →</Link>
      </p>

      {!progression ? (
        <Card>
          <Empty>
            Aucune mesure pour l'instant. Rien n'est affiché tant que rien n'a été observé — un
            niveau inventé serait cru.
          </Empty>
        </Card>
      ) : (
        <>
          <CarteNiveau progression={progression} />

          <Card title="Par matière">
            {progression.subjects.map((subject) => (
              <Gauge
                key={subject.subject}
                label={etiquette(subject.subject, subject.label)}
                score={subject.score}
                expected={subject.expected}
                observations={subject.observations}
              />
            ))}
          </Card>

          <Card title="Ce qui est fragile" tone="attention">
            <CompetencyList entries={fragile(progression)} empty="Rien de fragile en ce moment." />
          </Card>

          <Card title="Ce qui est en avance" tone="good">
            <CompetencyList entries={ahead(progression)} empty="Rien au-dessus de l'attendu pour l'instant." />
          </Card>

          <CarteVuEnClasse progression={progression} childId={childId} onValide={charger} />

          <CarteComplete progression={progression} />
        </>
      )}
    </Screen>
  );
}

/**
 * La carte complète et ses filtres.
 *
 * Un composant à part, et pas seulement par propreté : `ChildDetailScreen` rend
 * tôt sur l'erreur et le chargement, et un `useState` posé après ces retours
 * serait un appel conditionnel de hook.
 *
 * **Les matières proposées viennent de `progression.subjects`**, la source qui
 * dessine déjà la carte « Par matière », plutôt que d'un parcours des
 * compétences : deux listes de matières construites séparément finissent par
 * diverger, et le filtre proposerait alors une matière que la carte du dessus
 * ignore.
 */
function CarteComplete({ progression }: { progression: Progression }) {
  const [filtre, setFiltre] = useState<FiltreCompetences>(SANS_FILTRE);

  const visibles = useMemo(
    () => filtrerCompetences(progression.competencies, filtre),
    [progression.competencies, filtre],
  );

  /* « Non évaluées » et un état de maîtrise ne se composent pas : le second
     désigne une compétence mesurée. Le sélecteur est donc désactivé plutôt que
     laissé à produire une liste vide qu'aucun message n'expliquerait. */
  const etatMuet = filtre.mesure === "non-evaluees";

  return (
    <details className="deep">
      <summary>La carte complète des compétences</summary>

      <div className="filtres">
        <label className="filtre">
          <span className="filtre__label">Matière</span>
          <select
            className="select"
            value={filtre.subject ?? ""}
            onChange={(event) =>
              setFiltre({ ...filtre, subject: event.target.value || null })
            }
          >
            <option value="">Toutes</option>
            {progression.subjects.map((subject) => (
              <option key={subject.subject} value={subject.subject}>
                {etiquette(subject.subject, subject.label)}
              </option>
            ))}
          </select>
        </label>

        <label className="filtre">
          <span className="filtre__label">Mesure</span>
          <select
            className="select"
            value={filtre.mesure}
            onChange={(event) => {
              const mesure = event.target.value as Mesure;
              setFiltre({
                ...filtre,
                mesure,
                state: mesure === "non-evaluees" ? null : filtre.state,
              });
            }}
          >
            <option value="toutes">Toutes</option>
            <option value="evaluees">Évaluées</option>
            <option value="non-evaluees">Non évaluées</option>
          </select>
        </label>

        <label className="filtre">
          <span className="filtre__label">État</span>
          <select
            className="select"
            value={filtre.state ?? ""}
            disabled={etatMuet}
            onChange={(event) =>
              setFiltre({
                ...filtre,
                state: isMasteryState(event.target.value) ? event.target.value : null,
              })
            }
          >
            <option value="">Tous</option>
            {ETATS_EVALUES.map((state) => (
              <option key={state} value={state}>
                {PARENT_STATE_LABEL[state]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="muted small">
        {resumeDuFiltre(visibles.length, progression.competencies.length)}
      </p>

      <CompetencyList
        entries={visibles}
        empty={
          filtreActif(filtre)
            ? "Aucune compétence ne répond à ces filtres."
            : "Aucune compétence suivie."
        }
      />
    </details>
  );
}

/**
 * « Où en est-elle ? », en une jauge.
 *
 * Demandée par Hedi le 6 septembre 2026 : *« je veux côté [parent] avoir une
 * indication du niveau actuel de l'enfant »*. Neuf jauges par matière répondent
 * à une autre question, et personne ne les moyenne de tête.
 *
 * **Les deux réserves sont écrites sous la jauge, pas déduites du dessin.**
 * Une position tirée d'une matière sur neuf, ou pondérée à poids égal faute de
 * coefficients importés, se lit autrement qu'un bulletin — et c'est la phrase
 * qui le dit, jamais la barre.
 *
 * Rien mesuré du tout : `Gauge` ne dessine aucune barre et écrit « pas encore
 * mesuré », parce que `observations` vaut zéro. C'est le contrat du composant,
 * et c'est pour cela que le score n'est pas masqué ici.
 */
function CarteNiveau({ progression }: { progression: Progression }) {
  const niveau = progression.overall;
  if (!niveau) return null;

  return (
    <Card title="Niveau actuel">
      <Gauge
        label="Toutes matières"
        score={niveau.score}
        expected={niveau.expected}
        observations={niveau.observations}
      />
      <p className="muted small">{resumeDuNiveau(niveau)}</p>
    </Card>
  );
}

/**
 * Ce que la classe a traité et que le tuteur n'a jamais mesuré.
 *
 * Demandée le même jour : *« je veux aussi avoir clairement la liste des
 * compétences travaillées en cours pas traité par l'application tuteur »*. La
 * carte complète mélange « pas encore vu en classe » et « vu en classe, jamais
 * travaillé ici » ; la première est normale en septembre, la seconde est ce
 * qu'il faut rattraper.
 *
 * **Une liste vide a trois causes**, et l'écran les distingue (`phraseDuVide`) :
 * une lecture de journée est encore en vol, rien n'a été rattaché à une
 * compétence (`total === 0`), ou tout ce que la classe a traité a été mesuré.
 * Dire la deuxième en pensant la première annonce une panne à un parent dont le
 * système marche ; dire la troisième en pensant la deuxième fait lire « tout va
 * bien » à un rattachement qui ne tourne pas.
 *
 * La liste **non vide** dit l'attente aussi : une lecture en cours peut encore
 * y ajouter des lignes, et un parent qui la croit close la lirait comme le
 * compte final.
 *
 * **Une seconde liste depuis le 15 septembre 2026**, demandée par Hedi : les
 * compétences que le découpage de l'année place à cette période et qu'aucune
 * source ne nomme. C'est la troisième source de `enseigne`, la seule que
 * personne ne signe, et elle décide déjà de ce qui est servable — le parent est
 * le seul à savoir où en est vraiment la classe quand l'école n'a rien saisi.
 * Deux listes et non une colonne de plus : fondues, la seconde se lirait avec
 * l'assurance de la première, et la distinction *est* ce que le parent vient
 * trancher.
 *
 * Le bouton écrit par la route qui existe déjà — celle de l'écran « Ce qui a été
 * vu », `POST /api/children/{child_id}/vu-en-classe`. Deux écritures symétriques
 * finiraient par diverger sur ce qu'elles auditent, et une déclaration n'entre
 * jamais dans `mastery` (règle 3) : elle ouvre le périmètre servable, elle ne
 * mesure rien.
 *
 * Le bouton lui-même est `ui/VuEnClasse`, partagé avec la V2 depuis le
 * 25 septembre 2026 — il y demande confirmation, et la demander ici aussi est le
 * bon sens : ce geste lève un « on n'a pas encore vu ça » posé par l'enfant.
 */
function CarteVuEnClasse({
  progression,
  childId,
  onValide,
}: {
  progression: Progression;
  childId: string;
  onValide: () => Promise<void>;
}) {
  const taught = progression.taught;

  if (!taught) return null;
  // Rien d'attesté, rien de probable : la carte n'a qu'une phrase à dire, et
  // c'est celle qui nomme laquelle des trois causes on lit.
  if (taught.unmeasured.length === 0 && taught.probables.length === 0) {
    return (
      <Card title="Vu en classe, jamais mesuré ici" tone="attention">
        <Empty>{phraseDuVide(taught)}</Empty>
      </Card>
    );
  }

  return (
    <Card title="Vu en classe, jamais mesuré ici" tone="attention">
      {taught.unmeasured.length === 0 ? (
        <p className="muted small">{phraseDuVide(taught)}</p>
      ) : (
        <>
          <p className="muted small">
            {taught.unmeasured.length} compétence{taught.unmeasured.length > 1 ? "s" : ""} sur{" "}
            {taught.total} vue{taught.total > 1 ? "s" : ""} en classe.
            {taught.pendingReadings > 0
              ? ` ${taught.pendingReadings} journée${taught.pendingReadings > 1 ? "s" : ""} encore en cours de lecture.`
              : ""}
          </p>
          <ul className="competency-list">
            {taught.unmeasured.map((entry) => (
              <li key={entry.competencyId} className="competency">
                <div className="competency__head">
                  <span className="competency__label">{entry.label}</span>
                  <span className="muted small">{etiquette(entry.subject)}</span>
                </div>
                {/* Pourquoi on la croit traitée : le libellé du professeur ou la
                    date d'un journal. Jamais un mot écrit par l'enfant — c'est
                    `core-api/enseigne` qui en répond, pas cet affichage. */}
                {entry.reason ? <p className="muted small">{entry.reason}</p> : null}
                {/* Et pourquoi aucun exercice ne lui est proposé. Sans cette
                    ligne, trois situations qui appellent trois gestes opposés
                    — générer, régler l'échelle, ne rien faire — se lisaient
                    exactement pareil : une compétence dans une liste. */}
                <p className="muted small">{pourquoiPasProposee(entry)}</p>
              </li>
            ))}
          </ul>
        </>
      )}

      {taught.probables.length > 0 ? (
        <>
          <h3 className="card__title">Probablement vue en classe</h3>
          <p className="muted small">
            Le programme les place à cette période, mais aucun professeur ne l'a écrit et
            l'enfant n'en a pas parlé. Vous êtes le seul à pouvoir le confirmer. Une
            confirmation ouvre le chapitre aux exercices du soir ; elle ne mesure rien, et
            elle lève un « on n'a pas encore vu ça » déclaré par l'enfant.
          </p>
          <ul className="competency-list">
            {taught.probables.map((entry) => (
              <li key={entry.competencyId} className="competency">
                <div className="competency__head">
                  <span className="competency__label">{entry.label}</span>
                  <span className="muted small">{etiquette(entry.subject)}</span>
                </div>
                {entry.reason ? <p className="muted small">{entry.reason}</p> : null}
                {/* Le même bouton, la même phrase et la même écriture que dans
                    la V2 (`ui/VuEnClasse`) : deux déclarations finiraient par ne
                    plus dire la même chose de ce qu'elles emportent — or ce
                    qu'elles emportent est de décider contre l'enfant. */}
                <VuEnClasse
                  childId={childId}
                  competencyId={entry.competencyId}
                  libelle={entry.label}
                  onDeclare={onValide}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </Card>
  );
}

/**
 * Ce qui empêche de la proposer, ou le fait que rien ne l'empêche.
 *
 * **« peut être proposée » et non « sera proposée »** : le plan du soir tient un
 * plafond par matière et ne sert que les journées visées. L'absence de pénurie
 * dit qu'il y a de quoi tirer, jamais qu'un tirage aura lieu — et une promesse
 * qu'un écran ne peut pas tenir se retourne au premier soir où elle ne l'est pas.
 */
export function pourquoiPasProposee(entry: TaughtCompetency): string {
  if (!entry.shortage) {
    return "Des exercices sont disponibles à sa difficulté : elle peut être proposée le soir.";
  }
  return phraseDePenurie(entry.shortage);
}

function CompetencyList({ entries, empty }: { entries: CompetencyReading[]; empty: string }) {
  if (entries.length === 0) return <Empty>{empty}</Empty>;
  return (
    <ul className="competency-list">
      {entries.map((entry) => (
        <li key={entry.competencyId} className="competency" data-uncertain={entry.observations < 5}>
          <div className="competency__head">
            <span className="competency__label">{entry.label}</span>
            <StateChip state={entry.state} audience="parent" />
          </div>
          <Gauge
            name={entry.label}
            score={entry.score}
            expected={entry.expected}
            observations={entry.observations}
          />
          {entry.due ? <p className="muted small">à réviser</p> : null}
        </li>
      ))}
    </ul>
  );
}
